// Раздел «Проценка установки»: смета монтажа по нормативам.
//
// ЗАЧЕМ ЭТОТ ЭКРАН. На брифинге (/estimate) монтаж считается по каталогу нормативов —
// единственному в компании. Но сохранённые там сметы живут в localStorage браузера:
// проценка на 700 тысяч видна одному человеку и умирает вместе с профилем. Этот экран —
// тот же каталог и тот же расчёт, но из базы ядра (таблицы est_*, estimate_api.py).
//
// ИТОГ СЧИТАЕТ ЯДРО, А НЕ ЭТОТ ФАЙЛ. На портале итог у каждого браузера свой. Здесь
// НИ ОДНОГО умножения цены нет намеренно: экран собирает количества, отправляет их в
// GET /estimate/calc и печатает ответ. Число одно на всех; своя арифметика на фронте
// однажды разошлась бы с ядром на округлении, и у сметы стало бы два итога.
//
// СТАВКА — У СИСТЕМЫ, А НЕ ОДНА. В каталоге 2026-06-10 ставок две (4 500 и 4 950 ₽/НЧ
// у разных систем). Экран печатает ставку из данных системы и нигде не держит свою.
//
// КНОПКА «СОХРАНИТЬ» ЕСТЬ, А ЗАПИСИ НЕТ, И ЭКРАН ГОВОРИТ ЭТО ВСЛУХ. Разделы CRM выходят
// сначала только на чтение: ручки записи в ядре нет вовсе (см. estimate_api.py), включение —
// отдельный шаг и отдельное решение владельца. Кнопка выключена, и рядом написано почему:
// молчаливо неработающая кнопка хуже отсутствующей — человек нажмёт, ничего не произойдёт,
// и он уйдёт уверенный, что смета сохранена. (Тот же порядок, что в BpeTasks.tsx.)
//
// БЕЗЫМЯННЫЙ ПЕРВЫЙ РАЗДЕЛ — НОРМА ФАЙЛА. У каждой системы каталога первый раздел без
// названия (вводные позиции до первого заголовка); портал показывает его без шапки,
// и этот экран — так же. Выдуманное «Общие» сделало бы дерево незнакомым.
//
// ТРАНСПОРТ ЗДЕСЬ ЖЕ, А НЕ В src/api — раздел пока один экран; когда включат запись,
// он переедет в api/estimate.ts вместе с ручкой сохранения.

import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

import { CoreUnsupported, Unauthorized } from '../api/client'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtMoney, fmtText, pluralN } from '../lib/format'

// ───────────────────────────── транспорт ─────────────────────────────

const БАЗА = '/api/estimate'

/** Тот же разбор ответа, что в api/client.ts и BpeTasks.tsx. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 от ядра — отказ по существу (неверный расчёт), а не «ничего нет».
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.why as string) || (rec.error as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

const ОБЩЕЕ = { staleTime: 60_000, retry: 1 } as const

// ─────────── формы ответов ядра (см. estimate_api.py) ───────────

interface Версия {
  version: string
  loaded_at: string | null
  systems_n: number
  sections_n: number
  items_n: number
  is_current: boolean
}

interface Справочники {
  versions: Версия[]
  current_version: string | null
  rates: { rate: number; systems_n: number }[]
  counts: { estimates?: number; authors?: number; last_saved_at?: string | null }
  write_enabled: boolean
  write_why: string
  caveats: Record<string, string>
}

interface Позиция {
  id: number
  ord: number
  label: string
  hours: number
  mat: number
  other: number
}

interface Раздел {
  id: number
  ord: number
  title: string | null
  items: Позиция[]
}

interface Система {
  id: number
  ord: number
  name: string
  rate: number
  sections: Раздел[]
}

interface Каталог {
  version: string
  systems: Система[]
  systems_n: number
  sections_n: number
  items_n: number
}

interface СтрокаРасчёта {
  item_id: number
  label: string
  qty: number
  system_id: number
  system_name: string
  hours: number
  rate: number
  work: number
  mat: number
  other: number
  total: number
  manual_price: number | null
}

interface Расчёт {
  version: string
  rows: СтрокаРасчёта[]
  by_system: { system_id: number; name: string; rate: number; hours: number;
    total: number; lines: number }[]
  totals: { hours: number; work: number; mat: number; other: number; sub: number;
    adj_pct: number; adj: number; trip: number; grand: number }
  computed_by: string
}

interface ШапкаСметы {
  id: number
  catalog_version: string
  deal_id: string | null
  vessel: string | null
  imo: string | null
  client: string | null
  title: string | null
  author: string
  author_name: string | null
  adj_pct: number
  trip: number
  hours_total: number
  grand_total: number
  created_at: string | null
}

interface СписокСмет {
  rows: ШапкаСметы[]
  total: number
  next: number | null
  start: number
  why_empty: string | null
  write_why: string
}

interface СтрокаСметы {
  id: number
  item_id: number
  qty: number
  manual_price: number | null
  hours: number
  rate: number
  total: number
  label: string
  section_title: string | null
  system_name: string
}

interface КарточкаСметы {
  row: ШапкаСметы & { lines: СтрокаСметы[]; sub_total: number; adj_amount: number }
}

// ─────────── хуки ───────────

const useСправочники = () =>
  useQuery({ queryKey: ['estimate', 'refs'], queryFn: () => запрос<Справочники>('/refs'), ...ОБЩЕЕ })

const useКаталог = () =>
  useQuery({ queryKey: ['estimate', 'catalog'], queryFn: () => запрос<Каталог>('/catalog'), ...ОБЩЕЕ })

/** Расчёт зовётся ТОЛЬКО когда есть строки: пустой lines ядро честно отвергает (400). */
const useРасчёт = (строкаЗапроса: string, включено: boolean) =>
  useQuery({
    queryKey: ['estimate', 'calc', строкаЗапроса],
    queryFn: () => запрос<Расчёт>('/calc?' + строкаЗапроса),
    enabled: включено,
    ...ОБЩЕЕ,
  })

const useСметы = () =>
  useQuery({ queryKey: ['estimate', 'list'], queryFn: () => запрос<СписокСмет>('/list'), ...ОБЩЕЕ })

const useСмета = (id: number | null) =>
  useQuery({
    queryKey: ['estimate', 'doc', id],
    queryFn: () => запрос<КарточкаСметы>(`/doc?id=${id ?? 0}`),
    enabled: !!id,
    ...ОБЩЕЕ,
  })

// ───────────────────────────── подписи ─────────────────────────────

/** НЧ — как fmtH портала: до сотых, локаль ru-RU. */
const fmtНЧ = (n: number | null | undefined): string =>
  (n === null || n === undefined ? '—'
    : `${(Math.round(n * 100) / 100).toLocaleString('ru-RU')} НЧ`)

/** Та же раскладка «поля в ряд», что в BpeTasks.tsx (см. пояснение там же). */
const РЯД = {
  display: 'flex', gap: 'var(--s3)', flexWrap: 'wrap', alignItems: 'flex-end',
} as const

// ───────────────────────────── экран ─────────────────────────────

type Вкладка = 'calc' | 'saved'

export default function Estimate() {
  const [вкладка, setВкладка] = useState<Вкладка>('calc')
  const спр = useСправочники()
  const с = спр.data

  return (
    <>
      <div className="page-head">
        <h1>Проценка установки</h1>
        <span className="spacer" />
        <span className="hint">
          {с?.current_version ? `каталог нормативов ${с.current_version}` : ''}
          {с?.counts?.estimates
            ? ` · ${pluralN(с.counts.estimates, 'смета', 'сметы', 'смет')}` : ''}
        </span>
      </div>

      {/* Оговорки приходят от ядра и стоят ДО данных: спрятать их — соврать молча. */}
      {с && (
        <div className="notice" style={{ marginBottom: 16 }}>
          <b>Как это читать.</b>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            <li>{с.caveats.rates}</li>
            <li>{с.caveats.totals}</li>
            <li>{с.caveats.versions}</li>
          </ul>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={РЯД}>
          <button type="button" onClick={() => setВкладка('calc')} disabled={вкладка === 'calc'}>
            Расчёт
          </button>
          <button type="button" onClick={() => setВкладка('saved')} disabled={вкладка === 'saved'}>
            Сохранённые сметы
          </button>
        </div>
      </div>

      {вкладка === 'calc' && <РасчётСметы спр={с} />}
      {вкладка === 'saved' && <Сохранённые спр={с} />}
    </>
  )
}

// ───────────────────────── вкладка «Расчёт» ─────────────────────────

function РасчётСметы({ спр }: { спр?: Справочники }) {
  const кат = useКаталог()
  const каталог = кат.data

  // Шапка будущей сметы. Автор НЕ спрашивается: его подставит BFF из сессии,
  // когда запись включат, — форма с полем «автор» врала бы про устройство.
  const [vessel, setVessel] = useState('')
  const [imo, setImo] = useState('')
  const [deal, setDeal] = useState('')
  const [client, setClient] = useState('')
  const [adj, setAdj] = useState('')
  const [trip, setTrip] = useState('')

  // Выбранные системы и количества по позициям.
  const [выбраны, setВыбраны] = useState<number[]>([])
  const [колво, setКолво] = useState<Record<number, string>>({})

  const строкаLines = useMemo(() => {
    const куски: string[] = []
    for (const [id, значение] of Object.entries(колво)) {
      const q = parseFloat(значение)
      if (Number.isFinite(q) && q > 0) куски.push(`${id}:${q}`)
    }
    return куски.join(';')
  }, [колво])

  const строкаРасчёта = useMemo(() => {
    const p = new URLSearchParams()
    p.set('lines', строкаLines)
    if (каталог) p.set('version', каталог.version)
    const a = parseFloat(adj)
    if (Number.isFinite(a) && a !== 0) p.set('adj', String(a))
    const t = parseFloat(trip)
    if (Number.isFinite(t) && t > 0) p.set('trip', String(t))
    return p.toString()
  }, [строкаLines, adj, trip, каталог])

  // Расчёт уходит в ядро с паузой: человек печатает количество, а не по букве в запрос.
  const [отложено, setОтложено] = useState('')
  useEffect(() => {
    const т = setTimeout(() => setОтложено(строкаLines ? строкаРасчёта : ''), 400)
    return () => clearTimeout(т)
  }, [строкаРасчёта, строкаLines])

  const расчёт = useРасчёт(отложено, отложено !== '')
  const р = расчёт.data
  const поПозиции = useMemo(() => {
    const м = new Map<number, СтрокаРасчёта>()
    for (const с of р?.rows ?? []) м.set(с.item_id, с)
    return м
  }, [р])

  const переключить = (id: number) =>
    setВыбраны((в) => (в.includes(id) ? в.filter((x) => x !== id) : [...в, id]))

  return (
    <QueryState isLoading={кат.isLoading} error={кат.error}>
      {каталог && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <b>Смета</b>
            <div style={{ ...РЯД, marginTop: 8 }}>
              <div className="f" style={{ minWidth: 180 }}>
                <label htmlFor="estv">Судно</label>
                <input id="estv" value={vessel} onChange={(e) => setVessel(e.target.value)} />
              </div>
              <div className="f">
                <label htmlFor="esti">IMO</label>
                <input id="esti" value={imo} onChange={(e) => setImo(e.target.value)}
                       placeholder="9123456" />
              </div>
              <div className="f">
                <label htmlFor="estd">Сделка</label>
                <input id="estd" value={deal} onChange={(e) => setDeal(e.target.value)} />
              </div>
              <div className="f" style={{ minWidth: 180 }}>
                <label htmlFor="estc">Заказчик</label>
                <input id="estc" value={client} onChange={(e) => setClient(e.target.value)} />
              </div>
              <div className="f">
                <label htmlFor="esta">Скидка/наценка, %</label>
                <input id="esta" type="number" value={adj} placeholder="0"
                       onChange={(e) => setAdj(e.target.value)} />
              </div>
              <div className="f">
                <label htmlFor="estt">Командировочные, ₽</label>
                <input id="estt" type="number" value={trip} placeholder="0"
                       onChange={(e) => setTrip(e.target.value)} />
              </div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <b>Системы</b>
            <div className="hint" style={{ marginTop: 4 }}>
              Каталог {каталог.version}: {pluralN(каталог.systems_n, 'система', 'системы', 'систем')},{' '}
              {pluralN(каталог.items_n, 'позиция', 'позиции', 'позиций')}. Ставка нормо-часа —
              у каждой системы своя, она написана на системе.
            </div>
            <div style={{ ...РЯД, marginTop: 8 }}>
              {каталог.systems.map((s) => (
                <button key={s.id} type="button" onClick={() => переключить(s.id)}
                        className={выбраны.includes(s.id) ? 'primary' : ''}>
                  {s.name} · {fmtMoney(s.rate)}/НЧ
                </button>
              ))}
            </div>
          </div>

          {выбраны.length === 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="empty">
                <div className="empty-title">Выберите системы</div>
                <div className="empty-hint">
                  Дерево позиций откроется по выбранным системам — как на брифинге.
                </div>
              </div>
            </div>
          )}

          {каталог.systems.filter((s) => выбраны.includes(s.id)).map((s) => (
            <СистемаБлок key={s.id} система={s} колво={колво} setКолво={setКолво}
                         поПозиции={поПозиции} />
          ))}

          <Итог р={р} считается={расчёт.isFetching} естьСтроки={строкаLines !== ''}
                ошибка={расчёт.error} />

          <Сохранение
            спр={спр}
            естьСтроки={строкаLines !== ''}
            собрать={() => ({
              catalog_version: каталог?.version ?? '',
              lines: Object.entries(колво)
                .map(([id, з]) => ({ item_id: Number(id), qty: parseFloat(з) }))
                .filter((л) => Number.isFinite(л.qty) && л.qty > 0),
              ...(parseFloat(adj) ? { adj_pct: parseFloat(adj) } : {}),
              ...(parseFloat(trip) > 0 ? { trip: parseFloat(trip) } : {}),
              ...(vessel.trim() ? { vessel: vessel.trim() } : {}),
              ...(imo.trim() ? { imo: imo.trim() } : {}),
              ...(client.trim() ? { client: client.trim() } : {}),
              ...(deal.trim() ? { deal_id: Number(deal) || undefined } : {}),
            })}
          />
        </>
      )}
    </QueryState>
  )
}

function СистемаБлок({ система, колво, setКолво, поПозиции }: {
  система: Система
  колво: Record<number, string>
  setКолво: (f: (v: Record<number, string>) => Record<number, string>) => void
  поПозиции: Map<number, СтрокаРасчёта>
}) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="page-head" style={{ marginBottom: 4 }}>
        <b>{система.name}</b>
        <span className="spacer" />
        <span className="hint">ставка {fmtMoney(система.rate)}/НЧ</span>
      </div>
      <div className="table-wrap cards">
        <table>
          <thead>
            <tr>
              <th>Позиция</th>
              <th className="num nowrap">Норматив, НЧ</th>
              <th className="num nowrap">Кол-во</th>
              <th className="num nowrap">НЧ</th>
              <th className="num nowrap">Сумма</th>
            </tr>
          </thead>
          <tbody>
            {система.sections.map((sec) => (
              <РазделСтроки key={sec.id} раздел={sec} колво={колво} setКолво={setКолво}
                            поПозиции={поПозиции} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function РазделСтроки({ раздел, колво, setКолво, поПозиции }: {
  раздел: Раздел
  колво: Record<number, string>
  setКолво: (f: (v: Record<number, string>) => Record<number, string>) => void
  поПозиции: Map<number, СтрокаРасчёта>
}) {
  return (
    <>
      {/* Безымянный первый раздел показывается БЕЗ шапки — так в файле нормативов
          и так на портале. Выдуманный заголовок сделал бы дерево незнакомым. */}
      {раздел.title && (
        <tr>
          <td colSpan={5} className="muted"
              style={{ textTransform: 'uppercase', fontSize: 'var(--fs-hint)' }}>
            {раздел.title}
          </td>
        </tr>
      )}
      {раздел.items.map((п) => {
        const считано = поПозиции.get(п.id)
        return (
          <tr key={п.id}>
            <td data-label="Позиция">{п.label}</td>
            <td className="num nowrap" data-label="Норматив, НЧ">
              {(Math.round(п.hours * 100) / 100).toLocaleString('ru-RU')}
            </td>
            <td className="num nowrap" data-label="Кол-во">
              <input type="number" min={0} step={1} value={колво[п.id] ?? ''}
                     aria-label={`Количество: ${п.label}`}
                     style={{ width: 72, textAlign: 'right' }}
                     onChange={(e) => {
                       const v = e.target.value
                       setКолво((к) => {
                         const след = { ...к }
                         if (v === '' || parseFloat(v) <= 0) delete след[п.id]
                         else след[п.id] = v
                         return след
                       })
                     }} />
            </td>
            {/* НЧ и сумма строки — из ответа ядра, а не из умножения здесь: расчёт один. */}
            <td className="num nowrap" data-label="НЧ">
              {считано ? fmtНЧ(считано.hours) : '—'}
            </td>
            <td className="num nowrap" data-label="Сумма">
              {считано ? fmtMoney(считано.total) : '—'}
            </td>
          </tr>
        )
      })}
    </>
  )
}

function Итог({ р, считается, естьСтроки, ошибка }: {
  р?: Расчёт
  считается: boolean
  естьСтроки: boolean
  ошибка: unknown
}) {
  if (!естьСтроки) {
    return (
      <div className="card" style={{ marginBottom: 16 }}>
        <b>Итог</b>
        <div className="hint" style={{ marginTop: 6 }}>
          Проставьте количества — итог посчитает ядро, и он будет один на всех.
        </div>
      </div>
    )
  }
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="page-head" style={{ marginBottom: 4 }}>
        <b>Итог</b>
        <span className="spacer" />
        <span className="hint">
          {считается ? 'ядро считает…' : р ? 'посчитано ядром — число одно на всех' : ''}
        </span>
      </div>
      {ошибка ? (
        <div className="notice">Расчёт не прошёл: {String((ошибка as Error).message ?? ошибка)}</div>
      ) : р ? (
        <>
          {р.by_system.length > 1 && (
            <div className="table-wrap cards" style={{ marginBottom: 8 }}>
              <table>
                <thead>
                  <tr>
                    <th>Система</th>
                    <th className="num nowrap">Строк</th>
                    <th className="num nowrap">НЧ</th>
                    <th className="num nowrap">Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {р.by_system.map((s) => (
                    <tr key={s.system_id}>
                      <td data-label="Система">{s.name}</td>
                      <td className="num" data-label="Строк">{s.lines}</td>
                      <td className="num nowrap" data-label="НЧ">{fmtНЧ(s.hours)}</td>
                      <td className="num nowrap" data-label="Сумма">{fmtMoney(s.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={РЯД}>
            <span>Работы: <b>{fmtНЧ(р.totals.hours)}</b> · {fmtMoney(р.totals.work)}</span>
            <span>Материалы: {fmtMoney(р.totals.mat)}</span>
            <span>Прочее: {fmtMoney(р.totals.other)}</span>
            {р.totals.adj !== 0 && (
              <span>
                {р.totals.adj_pct > 0 ? 'Наценка' : 'Скидка'} {Math.abs(р.totals.adj_pct)}%:{' '}
                {fmtMoney(р.totals.adj)}
              </span>
            )}
            {р.totals.trip > 0 && <span>Командировочные: {fmtMoney(р.totals.trip)}</span>}
            {/* Фолбэк «1.2em» убран: токен --fs-h2 есть всегда, а литеральный
                запасной размер — как раз тот разнобой, от которого токены заведены. */}
            <span style={{ fontSize: 'var(--fs-h2)' }}>
              Итого: <b>{fmtMoney(р.totals.grand)}</b>
            </span>
          </div>
        </>
      ) : (
        <div className="hint">ядро считает…</div>
      )}
    </div>
  )
}

/** Кнопка сохранения. Запись включена 30.07.2026: смета уходит в /api/estimate/save,
 *  итоги считает ядро (присланные клиентом оно отвергает), автора подставляет BFF из
 *  сессии. Итог нажатия виден словами: номер сметы или отказ причиной — нажатие без
 *  последствий человек читает как «сохранил». */
function Сохранение({ спр, естьСтроки, собрать }: {
  спр?: Справочники; естьСтроки: boolean; собрать: () => Record<string, unknown>
}) {
  const можно = спр?.write_enabled ?? false
  const [идёт, setИдёт] = useState(false)
  const [итог, setИтог] = useState<string | null>(null)
  const [беда, setБеда] = useState<string | null>(null)

  async function сохранить() {
    setИдёт(true)
    setИтог(null)
    setБеда(null)
    try {
      const r = await fetch('/api/estimate/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(собрать()),
      })
      const d = (await r.json()) as { id?: number; error?: string; why?: string }
      if (r.ok && d.id) {
        setИтог(`Смета сохранена: № ${d.id}`)
      } else {
        setБеда(d.why || d.error || `ядро ответило ${r.status}`)
      }
    } catch (e: unknown) {
      setБеда(e instanceof Error ? e.message : String(e))
    } finally {
      setИдёт(false)
    }
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      {!можно && спр && (
        <div className="notice" style={{ marginBottom: 8 }}>
          <b>Смета отсюда пока не сохраняется.</b>
          <div style={{ marginTop: 6 }}>{спр.write_why}</div>
        </div>
      )}
      <button type="button" disabled={!можно || !естьСтроки || идёт}
              onClick={() => void сохранить()}
              title={можно ? undefined : спр?.write_why ?? undefined}>
        {идёт ? 'Сохраняем…' : 'Сохранить смету'}
      </button>
      {итог ? <span className="hint" style={{ marginLeft: 8 }}>{итог}</span> : null}
      {беда ? (
        <span className="hint" style={{ marginLeft: 8 }}>Не сохранилось: {беда}</span>
      ) : null}
    </div>
  )
}

// ───────────────────────── вкладка «Сохранённые сметы» ─────────────────────────

function Сохранённые({ спр }: { спр?: Справочники }) {
  const зпр = useСметы()
  const д = зпр.data
  const [открыта, setОткрыта] = useState<number | null>(null)

  return (
    <QueryState isLoading={зпр.isLoading} error={зпр.error}>
      <div className="card">
        <div className="table-wrap cards">
          <table>
            <thead>
              <tr>
                <th className="nowrap">Когда</th>
                <th>Судно</th>
                <th>Заказчик</th>
                <th className="nowrap">Сделка</th>
                <th>Автор</th>
                <th className="num nowrap">НЧ</th>
                <th className="num nowrap">Итого</th>
                <th className="nowrap">Каталог</th>
                <th className="nowrap" aria-label="открыть" />
              </tr>
            </thead>
            <tbody>
              {(д?.rows ?? []).map((р) => (
                <tr key={р.id}>
                  <td className="nowrap" data-label="Когда">{fmtDate(р.created_at)}</td>
                  <td data-label="Судно">
                    {fmtText(р.vessel)}
                    {р.imo && <div className="muted">IMO {р.imo}</div>}
                  </td>
                  <td data-label="Заказчик">{fmtText(р.client)}</td>
                  <td className="nowrap" data-label="Сделка">{fmtText(р.deal_id)}</td>
                  <td data-label="Автор">{fmtText(р.author_name ?? р.author)}</td>
                  <td className="num nowrap" data-label="НЧ">{fmtНЧ(р.hours_total)}</td>
                  <td className="num nowrap" data-label="Итого">{fmtMoney(р.grand_total)}</td>
                  {/* Версия каталога — не служебный мусор: по ней смету можно проверить
                      и после обновления нормативов. */}
                  <td className="nowrap" data-label="Каталог">{р.catalog_version}</td>
                  {/* data-label пустой: у колонки нет названия и в шапке. */}
                  <td className="nowrap" data-label="">
                    <button type="button" onClick={() => setОткрыта(р.id)}>Открыть</button>
                  </td>
                </tr>
              ))}
              {д && д.rows.length === 0 && (
                <tr>
                  <td colSpan={9} className="empty">
                    <div className="empty-title">Сохранённых смет здесь пока нет</div>
                    {/* Почему пусто — объясняет ядро, а не догадка экрана. */}
                    <div className="empty-hint">{д.why_empty ?? спр?.caveats.local}</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {открыта !== null && <Смета id={открыта} закрыть={() => setОткрыта(null)} />}
    </QueryState>
  )
}

function Смета({ id, закрыть }: { id: number; закрыть: () => void }) {
  const зпр = useСмета(id)
  const к = зпр.data?.row

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="page-head" style={{ marginBottom: 8 }}>
        <b>{к ? `Смета №${к.id} · ${fmtText(к.vessel)}` : 'Смета'}</b>
        <span className="spacer" />
        <button type="button" onClick={закрыть}>Закрыть</button>
      </div>
      <QueryState isLoading={зпр.isLoading} error={зпр.error}>
        {к && (
          <>
            <div className="hint" style={{ marginBottom: 8 }}>
              каталог {к.catalog_version} · автор {fmtText(к.author_name ?? к.author)} ·{' '}
              {fmtDate(к.created_at, true)}
            </div>
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th>Позиция</th>
                    <th>Система</th>
                    <th className="num nowrap">Кол-во</th>
                    <th className="num nowrap">НЧ</th>
                    <th className="num nowrap">Ставка</th>
                    <th className="num nowrap">Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {к.lines.map((л) => (
                    <tr key={л.id}>
                      <td data-label="Позиция">
                        {л.label}
                        {л.manual_price !== null && (
                          <span className="badge" style={{ marginLeft: 6 }}
                                title="цена задана вручную, в обход норматива">
                            ручная цена
                          </span>
                        )}
                      </td>
                      <td data-label="Система">{л.system_name}</td>
                      <td className="num" data-label="Кол-во">{л.qty}</td>
                      <td className="num nowrap" data-label="НЧ">{fmtНЧ(л.hours)}</td>
                      <td className="num nowrap" data-label="Ставка">{fmtMoney(л.rate)}</td>
                      <td className="num nowrap" data-label="Сумма">{fmtMoney(л.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Итоги — те, что ядро посчитало ПРИ СОХРАНЕНИИ. Смета — документ,
                при чтении не пересчитывается. */}
            <div style={{ ...РЯД, marginTop: 8 }}>
              <span>Всего: <b>{fmtНЧ(к.hours_total)}</b></span>
              {к.adj_amount !== 0 && (
                <span>{к.adj_pct > 0 ? 'Наценка' : 'Скидка'} {Math.abs(к.adj_pct)}%:{' '}
                  {fmtMoney(к.adj_amount)}</span>
              )}
              {к.trip > 0 && <span>Командировочные: {fmtMoney(к.trip)}</span>}
              <span>Итого: <b>{fmtMoney(к.grand_total)}</b></span>
            </div>
          </>
        )}
      </QueryState>
    </div>
  )
}
